import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Wallet, ArrowUpRight, ArrowDownLeft, Lock, Unlock, AlertTriangle, Loader2, RefreshCw, Clock, CheckCircle, XCircle } from 'lucide-react';
import api from '@/lib/api';
import { formatCurrency, formatDateTime } from '@/lib/utils';

const AdminEscrowTab = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [stats, setStats] = useState({ held: 0, released: 0, disputed: 0, total: 0 });
    const [actionDialog, setActionDialog] = useState({ open: false, order: null, action: null });
    const [actionReason, setActionReason] = useState('');
    const [processing, setProcessing] = useState(false);
    
    // Infinite scroll
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const perPage = 30;
    const observerRef = useRef(null);
    const loadMoreRef = useRef(null);

    const fetchEscrowOrders = useCallback(async (pageNum = 1, reset = false) => {
        try {
            if (pageNum === 1) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }
            
            const params = new URLSearchParams();
            params.append('skip', ((pageNum - 1) * perPage).toString());
            params.append('limit', perPage.toString());
            
            const res = await api.get(`/admin/escrow?${params.toString()}`);
            const newOrders = res.data.orders || [];
            const total = res.data.total || newOrders.length;
            
            if (reset || pageNum === 1) {
                setOrders(newOrders);
            } else {
                setOrders(prev => [...prev, ...newOrders]);
            }
            
            setStats(res.data.stats || { held: 0, released: 0, disputed: 0, total: 0 });
            setTotalCount(total);
            setHasMore(newOrders.length === perPage);
        } catch (error) {
            toast.error('Eroare la încărcarea datelor escrow');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, []);

    useEffect(() => {
        fetchEscrowOrders(1, true);
    }, []);

    // Intersection Observer for infinite scroll
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
                    setPage(prev => prev + 1);
                }
            },
            { threshold: 0.1 }
        );

        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        observerRef.current = observer;

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [hasMore, loadingMore, loading]);

    // Load more when page changes
    useEffect(() => {
        if (page > 1) {
            fetchEscrowOrders(page, false);
        }
    }, [page]);

    const handleRefresh = () => {
        setPage(1);
        fetchEscrowOrders(1, true);
    };

    const handleAction = async () => {
        if (!actionDialog.order || !actionDialog.action) return;
        
        setProcessing(true);
        try {
            await api.post(`/admin/escrow/${actionDialog.order.order_id}/${actionDialog.action}`, {
                reason: actionReason
            });
            toast.success(actionDialog.action === 'release' 
                ? 'Fonduri eliberate cu succes!' 
                : 'Fonduri returnate cu succes!');
            setActionDialog({ open: false, order: null, action: null });
            setActionReason('');
            handleRefresh();
        } catch (error) {
            const msg = typeof error.response?.data?.detail === 'string'
                ? error.response.data.detail
                : 'Eroare la procesarea acțiunii';
            toast.error(msg);
        } finally {
            setProcessing(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'held': { label: 'Blocat', color: 'bg-yellow-100 text-yellow-800', icon: Lock },
            'released': { label: 'Eliberat', color: 'bg-green-100 text-green-800', icon: Unlock },
            'disputed': { label: 'În dispută', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
            'refunded': { label: 'Returnat', color: 'bg-blue-100 text-blue-800', icon: ArrowDownLeft },
            'pending': { label: 'În așteptare', color: 'bg-gray-100 text-gray-800', icon: Clock },
        };
        const config = statusConfig[status] || statusConfig.pending;
        const Icon = config.icon;
        return (
            <Badge className={`${config.color} gap-1`}>
                <Icon className="w-3 h-3" />
                {config.label}
            </Badge>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold flex items-center gap-2">
                        <Wallet className="w-5 h-5" />
                        Gestiune Escrow
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Monitorizează și gestionează fondurile în escrow
                    </p>
                </div>
                <Button variant="outline" onClick={handleRefresh}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reîmprospătează
                </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                                <Lock className="w-5 h-5 text-yellow-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Fonduri blocate</p>
                                <p className="text-xl font-bold">{formatCurrency(stats.held)}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                                <ArrowUpRight className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Fonduri eliberate</p>
                                <p className="text-xl font-bold">{formatCurrency(stats.released)}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                <AlertTriangle className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">În dispută</p>
                                <p className="text-xl font-bold">{formatCurrency(stats.disputed)}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                <Wallet className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Total procesate</p>
                                <p className="text-xl font-bold">{formatCurrency(stats.total)}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Orders Table */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg">Tranzacții Escrow</CardTitle>
                    <CardDescription>
                        Comenzi cu fonduri în escrow sau care necesită acțiune
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {orders.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <Wallet className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>Nu există tranzacții escrow active</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Comandă</th>
                                        <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Produs</th>
                                        <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Vânzător</th>
                                        <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Cumpărător</th>
                                        <th className="text-right py-3 px-2 text-sm font-medium text-muted-foreground">Sumă</th>
                                        <th className="text-center py-3 px-2 text-sm font-medium text-muted-foreground">Status Escrow</th>
                                        <th className="text-center py-3 px-2 text-sm font-medium text-muted-foreground">Data</th>
                                        <th className="text-right py-3 px-2 text-sm font-medium text-muted-foreground">Acțiuni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.map(order => (
                                        <tr key={order.order_id} className="border-b hover:bg-muted/30">
                                            <td className="py-3 px-2">
                                                <span className="font-mono text-sm">{order.order_id.slice(0, 12)}...</span>
                                            </td>
                                            <td className="py-3 px-2">
                                                <span className="font-medium truncate max-w-[200px] block">{order.listing_title}</span>
                                            </td>
                                            <td className="py-3 px-2 text-sm">{order.seller_username}</td>
                                            <td className="py-3 px-2 text-sm">{order.buyer_username}</td>
                                            <td className="py-3 px-2 text-right font-medium">{formatCurrency(order.amount)}</td>
                                            <td className="py-3 px-2 text-center">
                                                {getStatusBadge(order.escrow_status)}
                                            </td>
                                            <td className="py-3 px-2 text-center text-sm text-muted-foreground">
                                                {formatDateTime(order.created_at)}
                                            </td>
                                            <td className="py-3 px-2 text-right">
                                                {(order.escrow_status === 'held' || order.escrow_status === 'disputed') && (
                                                    <div className="flex gap-1 justify-end">
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="text-green-600 hover:text-green-700 h-8"
                                                            onClick={() => setActionDialog({ open: true, order, action: 'release' })}
                                                        >
                                                            <ArrowUpRight className="w-3 h-3 mr-1" />
                                                            Eliberează
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="text-blue-600 hover:text-blue-700 h-8"
                                                            onClick={() => setActionDialog({ open: true, order, action: 'refund' })}
                                                        >
                                                            <ArrowDownLeft className="w-3 h-3 mr-1" />
                                                            Returnează
                                                        </Button>
                                                    </div>
                                                )}
                                                {order.escrow_status === 'released' && (
                                                    <Badge variant="outline" className="gap-1">
                                                        <CheckCircle className="w-3 h-3 text-green-500" />
                                                        Finalizat
                                                    </Badge>
                                                )}
                                                {order.escrow_status === 'refunded' && (
                                                    <Badge variant="outline" className="gap-1">
                                                        <XCircle className="w-3 h-3 text-blue-500" />
                                                        Returnat
                                                    </Badge>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            
                            {/* Infinite Scroll Loader */}
                            {hasMore && (
                                <div ref={loadMoreRef} className="flex justify-center py-4">
                                    {loadingMore && (
                                        <div className="flex items-center gap-2 text-muted-foreground">
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            <span>Se încarcă...</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {!hasMore && orders.length > 0 && (
                                <p className="text-center text-sm text-muted-foreground py-4">
                                    Ai ajuns la final ({totalCount} tranzacții)
                                </p>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Action Dialog */}
            <Dialog open={actionDialog.open} onOpenChange={(open) => setActionDialog({ ...actionDialog, open })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {actionDialog.action === 'release' ? 'Eliberează fonduri' : 'Returnează fonduri'}
                        </DialogTitle>
                        <DialogDescription>
                            {actionDialog.action === 'release' 
                                ? `Eliberezi ${formatCurrency(actionDialog.order?.amount)} către vânzătorul ${actionDialog.order?.seller_username}`
                                : `Returnezi ${formatCurrency(actionDialog.order?.amount)} către cumpărătorul ${actionDialog.order?.buyer_username}`}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="p-3 bg-muted/50 rounded-lg">
                            <p className="text-sm font-medium">{actionDialog.order?.listing_title}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                                Comandă: {actionDialog.order?.order_id}
                            </p>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Motiv (opțional)</label>
                            <Textarea
                                value={actionReason}
                                onChange={(e) => setActionReason(e.target.value)}
                                placeholder="Adaugă un motiv pentru această acțiune..."
                                rows={3}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setActionDialog({ open: false, order: null, action: null })}>
                            Anulează
                        </Button>
                        <Button 
                            onClick={handleAction}
                            disabled={processing}
                            className={actionDialog.action === 'release' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}
                        >
                            {processing ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : actionDialog.action === 'release' ? (
                                <ArrowUpRight className="w-4 h-4 mr-2" />
                            ) : (
                                <ArrowDownLeft className="w-4 h-4 mr-2" />
                            )}
                            {actionDialog.action === 'release' ? 'Eliberează' : 'Returnează'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminEscrowTab;
